import { toggleIdInList } from '@griply/utils';
import { useGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalTreeContextMenu } from 'shared/components/connected/goal-context-menu';
import {
  FormSelectTrigger,
  Size,
} from 'shared/components/ui/form-select-trigger';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';

import * as Styled from './goal-life-area-link-field.style';

export type GoalLifeAreaLinkFieldProps = {
  /** The goal being edited; absent on create. Drives cycle prevention in the picker. */
  goalId?: ID;
  parentIds: ID[];
  lifeAreaIds: ID[];
  onChangeParents: (parentIds: ID[]) => void;
  onChangeLifeAreas: (lifeAreaIds: ID[]) => void;
};

export const GoalLifeAreaLinkField: React.FC<GoalLifeAreaLinkFieldProps> = ({
  goalId,
  parentIds,
  lifeAreaIds,
  onChangeParents,
  onChangeLifeAreas,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const goals = useGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });

  const label = useMemo(() => {
    const names = [
      ...parentIds.flatMap(
        (id) => goals.find((goal) => goal.id === id)?.name ?? [],
      ),
      ...lifeAreaIds.flatMap(
        (id) => lifeAreas.find((lifeArea) => lifeArea.id === id)?.name ?? [],
      ),
    ];

    return names.length ? names.join(', ') : null;
  }, [parentIds, lifeAreaIds, goals, lifeAreas]);

  const handleToggleGoal = (id: ID) =>
    onChangeParents(toggleIdInList(parentIds, id));

  const handleToggleLifeArea = (id: ID) =>
    onChangeLifeAreas(toggleIdInList(lifeAreaIds, id));

  return (
    <Styled.Container ref={containerRef}>
      <FormSelectTrigger
        onClick={openMenu}
        isPlaceholder={!label}
        size={Size.ExtraSmall}
        noOverflow
      >
        {label ?? t('forms.goal.fields.parent.placeholder')}
      </FormSelectTrigger>

      {menuOpen && (
        <GoalTreeContextMenu
          id={goalId}
          onClose={closeMenu}
          referenceElement={containerRef}
          position="bottom"
          selectedGoalIds={parentIds}
          selectedLifeAreaIds={lifeAreaIds}
          onToggleGoal={handleToggleGoal}
          onToggleLifeArea={handleToggleLifeArea}
        />
      )}
    </Styled.Container>
  );
};
